import { authApi } from "@/domains/shared/apis/authApi";
import { Roles } from "@/domains/shared/models/roles";
import { useAuth } from "@/domains/shared/hooks/useAuth";
import { resolvePlanAdjustmentNoticeText } from "@/domains/subscription/resolvePlanAdjustmentNoticeText";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const planAdjustmentNoticeQueryKey = ["auth", "plan-adjustment-notice"] as const;

export function usePlanAdjustmentNotice() {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const hasActiveClinicMembership = (user?.clinicMemberships ?? []).some((membership) => membership.isActive);
    const enabled = user?.role === Roles.Therapist && !hasActiveClinicMembership;

    const query = useQuery({
        queryKey: planAdjustmentNoticeQueryKey,
        queryFn: async () => {
            const { data } = await authApi.getPlanAdjustmentNotice();
            return data;
        },
        enabled,
    });

    const dismissMutation = useMutation({
        mutationFn: () => authApi.dismissPlanAdjustmentNotice(),
        onSuccess: () => {
            queryClient.setQueryData(planAdjustmentNoticeQueryKey, { showBanner: false });
        },
    });

    const message = resolvePlanAdjustmentNoticeText({
        code: query.data?.code,
        message: query.data?.message,
        params: query.data?.params,
    });

    return {
        showBanner: enabled && Boolean(query.data?.showBanner) && Boolean(message),
        message,
        code: query.data?.code ?? null,
        adjustedAt: query.data?.adjustedAt ?? null,
        isLoading: query.isLoading,
        dismiss: () => dismissMutation.mutateAsync(),
        dismissing: dismissMutation.isPending,
    };
}
